import React, {useEffect, useState} from 'react';
import {
    Box,
    Fade,
    Modal,
    Button,
    Backdrop,
    Container,
    Typography,
} from '@material-ui/core';
import {Close as CloseIcon} from '@material-ui/icons';
import TypeSelect, {SelectProps} from '@pluto/enum-type-select';
import FileImportProvider from './FileImportProvider';
import FileImportFooter from './FileImportFooter';
import Divider from './Divider';
import {
    handleLoad,
    handleOpen,
    handleClear,
    handleClose,
    handleSubmit,
    handleFileLoad,
    createFileReader,
    handleChange,
    handleOptsChange,
} from './FileImportContainerHandlers';
import ImageImportPreview from './ImageImportPreview';
import FileImportPreview from './FileImportPreview';
import {Options, PreviewComponentType} from './types';

interface Props {
    modalTitle: string;
    btnText: string;
    submitBtnText?: string;
    btnClass: string;
    action: Function;
    acceptedFiles: string[];
    previewComponent: PreviewComponentType;
    selectOpts: SelectProps[];
    className?: string;
}

export default function FileImportContainer({
    modalTitle,
    btnText,
    submitBtnText = 'Add',
    btnClass,
    action,
    acceptedFiles,
    selectOpts,
    previewComponent,
    className,
}: Props) {
    const getDefaultOpts = (opts: SelectProps[]) =>
        opts.reduce(
            (acc, {enumType, defaultValue}: SelectProps) => ({
                ...acc,
                [enumType]: defaultValue,
            }),
            {}
        );
    const defaultOpts = getDefaultOpts(selectOpts);
    const [open, setOpen] = useState<boolean>(false);
    const [opt, setOpt] = useState<Options>(defaultOpts);
    const [data, setData] = useState<File>();
    const [preview, setPreview] = useState<string>('');

    const onOpen = handleOpen(setOpen);
    const onFileLoaded = handleFileLoad(data, setPreview, createFileReader);
    const onClear = handleClear(setData, setPreview, setOpt, defaultOpts);
    const onClose = handleClose(setOpen, onClear);

    useEffect(onFileLoaded, [data]);

    const onFileLoad = handleLoad(setData);

    const onSubmit = handleSubmit(action, onClose, data, opt);

    const onChange = handleChange(setOpt, handleOptsChange);

    const disableSubmitBtn =
        !preview || Object.values(opt).filter(Boolean).length !== selectOpts.length;

    return (
        <Box className="import">
            <Button onClick={onOpen} variant="outlined" size="small" className={btnClass}>
                {btnText}
            </Button>
            <Modal
                aria-labelledby="transition-modal-title"
                className="import__modal"
                open={open}
                onClose={onClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Container className="import__modal-container">
                        <div className="import__modal-header">
                            <Typography variant="h5" id="import-modal-title">
                                {modalTitle}
                            </Typography>
                            <CloseIcon onClick={onClose} />
                        </div>
                        <Divider />
                        <FileImportProvider
                            handleFileLoad={onFileLoad}
                            handleClear={onClear}
                            preview={preview}
                            data={data}
                            acceptedFiles={acceptedFiles}
                            previewComponent={previewComponent}
                            className={className}
                        />
                        <div className="import__modal-select">
                            {selectOpts.map(
                                ({placeholder, enumType, sortOrder, disabled}: SelectProps) => (
                                    <TypeSelect
                                        disabled={disabled}
                                        value={opt[enumType]}
                                        onChange={onChange}
                                        placeholder={placeholder}
                                        enumType={enumType}
                                        key={enumType}
                                        sortOrder={sortOrder}
                                    />
                                )
                            )}
                        </div>
                        <Divider />
                        <FileImportFooter
                            disable={disableSubmitBtn}
                            handleClose={onClose}
                            handleSubmit={onSubmit}
                            btnText={submitBtnText}
                        />
                    </Container>
                </Fade>
            </Modal>
        </Box>
    );
}

FileImportContainer.ImagePreview = ImageImportPreview;
FileImportContainer.FilePreview = FileImportPreview;
